import React, { useState } from 'react';
import AppDetailsContainer from '../../../components/AppDetailsContainer';
import { Body } from '~/components/atom/Typography';
import MainPreview from '../MainPreview';
import { StepName } from '../MainPreview/types';
import IntlInputComponent from '../../../components/WaardecheckTemplate/components/IntlInputComponent';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AppEntryItemHeader from '../../../components/AppEntryItemHeader';
import { useRecoilState, useRecoilValue } from 'recoil';
import { biedwijzerContentState, biedwijzerInitialContentState } from '../..';
import { mergeDeepRight } from 'ramda';
import type { AppBbWaardecheckText, UnionImage } from '~/graphql/types';
import ImageOrAssetInput from '~/components/organism/ImageOrAssetInput';
import RichTextEditor from '~/components/organism/RichTextEditor';
import TextEditorContainer from '~/components/atom/TextEditorContainer';
import translate from 'translate';
import useUnionImageUrl from '~/hooks/useUnionImageUrl';
import { getDefaultValues } from '../../appConfig';
import TextButton from '~/components/atom/TextButton';
import AppInputLabel from '../../../components/InputLabel';

const text = {
  header: 'Over jouw aankoopdiensten',
  description:
    'Deze pagina wordt weergegeven in het adviesrapport om leads kennis te laten maken met jouw dienstverlening. Daarnaast wordt de tekst getoond wanneer er geen BiedWijzer kan worden opgesteld, omdat de betreffende woning bij jouw kantoor in de verkoop is.',
};

export type Props = RouteComponentProps & {};

const AboutUs: React.FCC<Props> = () => {
  const initialValues = useRecoilValue(biedwijzerInitialContentState);
  const [values, setValues] = useRecoilState(biedwijzerContentState);
  const [version, setVersion] = useState<Record<string, number>>({
    nl: 0,
    en: 0,
  });
  const aboutUsImageUrl = useUnionImageUrl({
    image: values?.content?.aboutUs.image,
    defaultImage: null,
  });

  const onUpdate = (
    fieldName: string,
    value: Partial<AppBbWaardecheckText> | UnionImage,
  ) => {
    setValues(prev => {
      if (!prev) return prev;
      const isString = typeof value === 'string';
      return {
        ...prev,
        content: prev.content
          ? {
              ...prev.content,
              aboutUs: {
                ...prev.content.aboutUs,
                [fieldName]: isString
                  ? value
                  : mergeDeepRight(prev.content.aboutUs[fieldName], value),
              },
            }
          : prev.content,
      };
    });
  };

  if (!values) return null;

  const onClickTranslate = async () => {
    if (!values?.content?.aboutUs?.body?.nl) return;

    const translatedBody = await translate(values?.content?.aboutUs?.body?.nl, {
      from: 'nl',
      to: 'en',
    });

    onUpdate('body', { en: translatedBody });
    setVersion(prev => ({ ...prev, en: prev.en + 1 }));
  };

  const onSetToDefaultText = (locale: 'nl' | 'en') => {
    onUpdate('body', {
      [locale]: getDefaultValues().aboutUs.body[locale],
    });
    setVersion(prev => ({ ...prev, [locale]: prev[locale] + 1 }));
  };

  return (
    <AppDetailsContainer header={text.header} icon="user">
      <Body>{text.description}</Body>
      <MainPreview
        step={StepName.AboutUs}
        values={values?.content}
        data={{
          appConfigData: {
            aboutUs: {
              image: {
                src: aboutUsImageUrl || '',
              },
              heading: {
                nl: values?.content?.aboutUs?.heading?.nl || '',
                en: values?.content?.aboutUs?.heading?.en || '',
              },
              body: {
                nl: values?.content?.aboutUs?.body?.nl || '',
                en: values?.content?.aboutUs?.body?.en || '',
              },
            },
          },
        }}
      />

      <ImageOrAssetInput
        title="Foto van aankoopmakelaar"
        description="Stel een foto in die wordt getoond op de pagina."
        initialImage={initialValues?.content?.aboutUs.image}
        currentImage={values?.content?.aboutUs.image}
        imageFileName="aboutUsImage"
        onParentUpdate={args => {
          if (!args) return;
          onUpdate('image', args);
        }}
      />

      <IntlInputComponent
        title="Koptekst"
        value={{
          nl: values?.content?.aboutUs?.heading?.nl,
          en: values?.content?.aboutUs?.heading?.en,
        }}
        onChange={heading => {
          onUpdate('heading', { nl: heading.nl, en: heading.en });
        }}
        defaultValue={{
          nl: getDefaultValues().aboutUs.heading.nl,
          en: getDefaultValues().aboutUs.heading.en,
        }}
      />

      <AppEntryItemHeader>Body</AppEntryItemHeader>

      <JustificationContainer direction="column" width="100%" gap="base">
        <JustificationContainer direction="column" width="100%">
          <AppInputLabel
            label="Tekst Nederlands"
            reset="Standaardtekst gebruiken"
            onReset={() => onSetToDefaultText('nl')}
          />

          <TextEditorContainer>
            <RichTextEditor
              value={values?.content?.aboutUs?.body?.nl || ''}
              onChange={value => {
                onUpdate('body', { nl: value });
              }}
              key={version.nl}
            />
          </TextEditorContainer>
        </JustificationContainer>

        <JustificationContainer width="100%" justification="center">
          <TextButton
            icon="repeat"
            onClick={onClickTranslate}
            label="Vertaal bovenstaande tekst naar Engels"
            appearance="secondary"
            width="max-content"
          />
        </JustificationContainer>

        <JustificationContainer direction="column" width="100%">
          <JustificationContainer direction="row" width="100%">
            <AppInputLabel
              label="Tekst Engels"
              reset="Standaardtekst gebruiken"
              onReset={() => onSetToDefaultText('en')}
            />
          </JustificationContainer>
          <TextEditorContainer>
            <RichTextEditor
              value={values?.content?.aboutUs?.body?.en || ''}
              onChange={value => {
                onUpdate('body', { en: value });
              }}
              key={version.en}
            />
          </TextEditorContainer>
        </JustificationContainer>
      </JustificationContainer>
    </AppDetailsContainer>
  );
};

export default AboutUs;
